import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["isActive", "level", "disabled", "iconLeft", "iconRight", "onClick", "title", "statusDot", "href", "role", "external", "tabIndex"],
  _excluded2 = ["onMouseEnter", "onMouseLeave"],
  _excluded3 = ["isActive", "level", "onClick", "title", "isTrellisCompatibleComponent"];
import { useCallback } from 'react';
import PropTypes from 'prop-types';
import UISideNavItem from 'UIComponents/nav/UISideNavItem';
import styled from 'styled-components';
import { SLINKY, EERIE, OLAF } from 'HubStyleTokens/colors';
import { Flex } from 'foundations-components/components/Flex';
import { ExternalLinkIcon } from 'foundations-assets';
import { StatusDot } from 'foundations-components/transitional/components/StatusDot';
import { filterProps } from 'foundations-components/utils/filterProps';
import { withNonTrellisComponent, withTrellisComponent } from '../hoc/withTrellisComponentHOCs';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const NavItemWrapper = styled.div.withConfig({
  displayName: "EPPatagoniaCategoryNavItem__NavItemWrapper",
  componentId: "sc-1v2bteb-0"
})([".patagonia-sidebar-category-item{padding:14px 12px 14px 12px;&.active-level-1{background-color:", ";color:", ";}&.active-level-2{background-color:", ";color:", ";}}"], SLINKY, OLAF, EERIE, OLAF);
const SideNavItemLabel = ({
  children
}) => {
  return /*#__PURE__*/_jsx(Flex, {
    alignItems: "center",
    justifyContent: "space-between",
    children: children
  });
};
const StyledLink = styled.a.withConfig({
  displayName: "EPPatagoniaCategoryNavItem__StyledLink",
  componentId: "sc-1v2bteb-1"
})(({
  theme: _theme,
  $isDisabled,
  $active,
  $depth
}) => {
  const theme = _theme;
  const hoverStyles = !$isDisabled ? {
    textDecoration: 'none',
    backgroundColor: $active ? undefined : theme.color['fill-secondary-subtle'],
    color: $active ? theme.color['text-primary-on-fill-default'] : theme.color['text-core-default']
  } : {};
  let activeStyles = {};
  if ($active && $depth === 1) {
    activeStyles = {
      backgroundColor: theme.color['fill-accent-neutral-hover'],
      color: theme.color['text-primary-on-fill-default']
    };
  } else if ($active && $depth === 2) {
    activeStyles = {
      backgroundColor: theme.color['icon-core-subtle'],
      color: theme.color['text-primary-on-fill-default']
    };
  } else if ($active) {
    activeStyles = {
      color: theme.color['text-core-default'],
      backgroundColor: theme.color['fill-secondary-subtle']
    };
  }
  return Object.assign({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    color: theme.color['text-core-default'],
    paddingInline: theme.space['300'],
    paddingBlock: theme.space['300'],
    position: 'relative',
    textDecoration: 'none',
    width: '100%',
    cursor: $isDisabled ? 'not-allowed' : 'pointer',
    transition: '0.15s all ease-out'
  }, theme.typography['body-100'], {
    '&&:hover': hoverStyles
  }, activeStyles);
});
const TrellisEPPatagoniaCategoryNavItemComp = _ref => {
  let {
      isActive = false,
      level = 1,
      disabled = false,
      iconLeft: IconLeft,
      iconRight: IconRight,
      onClick,
      title,
      statusDot,
      href,
      role = 'button',
      external,
      tabIndex = 0
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const {
    domProps
  } = filterProps(rest);
  const {
      onMouseEnter: onMouseEnterCallback,
      onMouseLeave: onMouseLeaveCallback
    } = domProps,
    otherDomProps = _objectWithoutPropertiesLoose(domProps, _excluded2);
  const onMouseEnter = useCallback(e => {
    onMouseEnterCallback === null || onMouseEnterCallback === void 0 || onMouseEnterCallback(e);
  }, [onMouseEnterCallback]);
  const onMouseLeave = useCallback(e => {
    onMouseLeaveCallback === null || onMouseLeaveCallback === void 0 || onMouseLeaveCallback(e);
  }, [onMouseLeaveCallback]);
  if (external) {
    IconRight = /*#__PURE__*/_jsx(ExternalLinkIcon, {
      color: "icon-core-default",
      size: "300"
    });
  }
  const handleClick = evt => {
    if (disabled) {
      evt.stopPropagation();
      evt.preventDefault();
      return;
    }
    onClick === null || onClick === void 0 || onClick(evt);
  };
  const handleKeyDown = evt => {
    const {
      key
    } = evt;
    if (key === ' ' || key === 'Space') {
      if (disabled) {
        evt.preventDefault();
        evt.stopPropagation();
        return;
      }
      handleClick(evt);

      // Prevent scrolling
      evt.preventDefault();
    }
  };
  return /*#__PURE__*/_jsx("li", {
    children: /*#__PURE__*/_jsxs(StyledLink, Object.assign({
      $active: isActive,
      $depth: level,
      "aria-current": !!isActive,
      "aria-disabled": disabled,
      $isDisabled: disabled,
      href: href,
      onMouseEnter: onMouseEnter,
      onMouseLeave: onMouseLeave,
      onClick: handleClick,
      onKeyDown: handleKeyDown,
      role: href ? 'link' : role,
      tabIndex: disabled ? -1 : tabIndex
    }, otherDomProps, {
      children: [/*#__PURE__*/_jsxs(Flex, {
        children: [statusDot && /*#__PURE__*/_jsx(Flex, {
          alignItems: "center",
          paddingInlineEnd: "100",
          children: /*#__PURE__*/_jsx(StatusDot, {
            use: statusDot
          })
        }), IconLeft && /*#__PURE__*/_jsx(Flex, {
          alignItems: "center",
          paddingInlineEnd: "100",
          children: typeof IconLeft === 'function' ? IconLeft() : IconLeft
        }), /*#__PURE__*/_jsx(SideNavItemLabel, {
          children: title
        })]
      }), /*#__PURE__*/_jsx(Flex, {
        columnGap: "200",
        children: IconRight && /*#__PURE__*/_jsx(Flex, {
          alignItems: "center",
          children: typeof IconRight === 'function' ? IconRight() : IconRight
        })
      })]
    }))
  });
};
const EPPatagoniaCategoryNavItem = _ref2 => {
  let {
      isActive = false,
      level = 1,
      onClick,
      title,
      isTrellisCompatibleComponent
    } = _ref2,
    rest = _objectWithoutPropertiesLoose(_ref2, _excluded3);
  if (isTrellisCompatibleComponent) {
    return /*#__PURE__*/_jsx(TrellisEPPatagoniaCategoryNavItemComp, Object.assign({
      isActive: isActive,
      level: level,
      onClick: onClick,
      title: title
    }, rest));
  }
  return /*#__PURE__*/_jsx(NavItemWrapper, {
    children: /*#__PURE__*/_jsx(UISideNavItem, Object.assign({
      title: title,
      className: isActive ? `patagonia-sidebar-category-item active-level-${level}` : 'patagonia-sidebar-category-item',
      onClick: onClick
    }, rest))
  });
};
EPPatagoniaCategoryNavItem.propTypes = {
  title: PropTypes.object.isRequired,
  onClick: PropTypes.func
};
export const LegacyEPPatagoniaCategoryNavItem = withNonTrellisComponent()(
// @ts-expect-error proptypes not playing nicely
EPPatagoniaCategoryNavItem);
export const TrellisEPPatagoniaCategoryNavItem = withTrellisComponent()(
// @ts-expect-error proptypes not playing nicely
EPPatagoniaCategoryNavItem);
export default TrellisEPPatagoniaCategoryNavItem;